import { color } from '../theme';

/**
 * Every logo position in the product renders this. The artwork is the company's own mark
 * (public/brand/the-conts-logo.png, cropped from their report), recoloured to the brand gold by
 * scripts/brand/make-logo-variants.py: mark-gold.png (the towers) and logo-gold.png (towers + THE CONTS).
 * The gold reads on black; on a light surface the towers sit on a black tile so they never wash out.
 */

export const MARK = { src: '/brand/mark-gold.png', w: 117, h: 136 };
const LOCKUP = { src: '/brand/logo-gold.png', w: 195, h: 162 };
/** Dream Crafter's building and bear, white with its gold bars (supply chain workspace), from the same script. */
const DC_MARK = { src: '/brand/dc-mark.png', w: 138, h: 160 };

/** Whose logo: The Conts (rental) or Dream Crafter (the supply chain workspace). */
export type LogoBrand = 'theConts' | 'dreamCrafter';
const BRAND_NAME: Record<LogoBrand, string> = { theConts: 'THE CONTS', dreamCrafter: 'DREAM CRAFTER' };

/** The towers' weight sits right of and below their bounding box (thin lines on the left, the block on the right),
 *  so a box-centred mark reads as off-centre in its tile. This nudge, as a share of the mark's own width and height,
 *  puts the visual centre in the middle; about half the measured offset of the artwork's centre of mass. */
export const MARK_OPTICAL = { x: -0.05, y: -0.02 };
export const markNudge = (h: number) => {
  const w = MARK.w * h / MARK.h;
  return `translate(${(MARK_OPTICAL.x * w).toFixed(2)}px, ${(MARK_OPTICAL.y * h).toFixed(2)}px)`;
};

export function Logo({ variant = 'full', size = 'md', onDark = false, product = false, brand = 'theConts' }: {
  variant?: 'full' | 'mark' | 'wordmark';
  size?: 'sm' | 'md' | 'lg';
  onDark?: boolean;
  /** The app's own lockup (sidebar): "Property OS" under the wordmark, as in the desktop design. */
  product?: boolean;
  brand?: LogoBrand;
}) {
  const dc = brand === 'dreamCrafter';
  const height = { sm: 28, md: 40, lg: 50 }[size];
  const ink = onDark ? '#FFFFFF' : color.ink;
  const muted = onDark ? color.dim : color.textMuted;

  // The large lockup on dark (the login hero) is the company's artwork itself.
  if (variant === 'full' && size === 'lg' && onDark && !product && !dc) {
    const h = 112;
    return <img src={LOCKUP.src} alt="The Conts" width={Math.round(LOCKUP.w * h / LOCKUP.h)} height={h} style={{ display: 'block' }} />;
  }

  const towers = (h: number) => dc
    ? <img src={DC_MARK.src} alt="" aria-hidden width={Math.round(DC_MARK.w * h / DC_MARK.h)} height={h} style={{ display: 'block', flex: 'none' }} />
    : (
    <img src={MARK.src} alt="" aria-hidden width={Math.round(MARK.w * h / MARK.h)} height={h} style={{ display: 'block', flex: 'none', transform: markNudge(h) }} />
  );
  // On dark there is no tile; the towers keep a little air inside their square so the lockup is not cramped.
  const mark = onDark
    ? <div aria-hidden style={{ width: height, height, flex: 'none', display: 'grid', placeItems: 'center' }}>{towers(Math.round(height * 0.9))}</div>
    : (
      <div aria-hidden style={{ width: height, height, flex: 'none', borderRadius: Math.round(height * 0.24), background: color.black, display: 'grid', placeItems: 'center' }}>
        {towers(Math.round(height * 0.72))}
      </div>
    );

  if (variant === 'mark') return <span role="img" aria-label={dc ? 'Dream Crafter' : 'The Conts'} style={{ display: 'inline-flex' }}>{mark}</span>;

  const wordmark = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: size === 'sm' ? 2 : 4 }}>
      <span style={{
        fontSize: size === 'sm' ? 12 : size === 'lg' ? 16 : 14,
        // DREAM CRAFTER is four letters longer, so it spaces tighter to fit the same sidebar width.
        fontWeight: 600, letterSpacing: dc ? '0.08em' : '0.18em', whiteSpace: 'nowrap', color: onDark ? '#FFFFFF' : ink,
      }}>{BRAND_NAME[brand]}</span>
      {product
        // The sidebar lockup has to fit beside the collapse button (about 136px); the design names the
        // product there instead of squeezing the tagline below the 11px minimum.
        ? <span style={{ fontSize: 11, lineHeight: 1.35, color: muted, whiteSpace: 'nowrap' }}>Property OS</span>
        : <span style={{
            fontSize: 11, letterSpacing: size === 'sm' ? '0.17em' : '0.26em', color: muted, whiteSpace: 'nowrap', // vocab-ok: the company's brand tagline, not a building
          }}>PROPERTY MANAGEMENT</span>}
    </div>
  );

  if (variant === 'wordmark') return wordmark;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: size === 'sm' ? 11 : 15 }}>
      {mark}
      {wordmark}
    </div>
  );
}
